/**
 * ProviderOSDocuments — paperwork inbox + sign workflow.
 *
 * Lists provider_documents, supports draft → out_for_sig → signed →
 * exchanged lifecycle. Hooks into existing GET / POST / route /
 * sign / exchange endpoints.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Plus, Send, CheckCircle2, ArrowUpRightFromSquare,
  Ban, Loader2, ArrowRight, Inbox,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProviderOSGate from '../components/provider_os/ProviderOSGate';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { v: 'F2F', l: 'Face-to-Face Encounter (F2F)' },
  { v: '485_POC', l: 'CMS 485 Plan of Care' },
  { v: 'NOA', l: 'Notice of Admission (NOA)' },
  { v: 'consent', l: 'Patient consent' },
  { v: 'intake', l: 'Intake packet' },
  { v: 'discharge_summary', l: 'Discharge summary' },
];

const StatusPill = ({ status }) => {
  const map = {
    draft:       { tone: 'bg-slate-100 text-slate-700',     label: 'Draft' },
    out_for_sig: { tone: 'bg-blue-100 text-blue-800',       label: 'Out for signature' },
    signed:      { tone: 'bg-emerald-100 text-emerald-800', label: 'Signed' },
    exchanged:   { tone: 'bg-emerald-200 text-emerald-900', label: 'Exchanged' },
    cancelled:   { tone: 'bg-red-100 text-red-800',         label: 'Cancelled' },
  };
  const m = map[status] || { tone: 'bg-slate-100 text-slate-700', label: status };
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${m.tone}`}>
      {m.label}
    </span>
  );
};

export default function ProviderOSDocuments() {
  return (
    <ProviderOSGate>
      <DocsInner />
    </ProviderOSGate>
  );
}

function DocsInner() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/provider/documents` + (filter !== 'all' ? `?status=${filter}` : '');
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setRows(res.data.rows || []);
    } catch (e) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  const callDocAction = async (id, action, body) => {
    try {
      await axios.post(`${API}/provider/documents/${id}/${action}`,
        body || {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${action.replace(/-/g, ' ')} ✓`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Action failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="provider-os-documents">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Paperwork</h1>
          <p className="text-sm text-slate-500 mt-1">
            F2F, 485 POC, NOA, consents — sign and exchange with the hospital.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/provider-os" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
            Dashboard <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            data-testid="create-doc-btn"
          >
            <Plus className="w-4 h-4" /> New document
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {['all', 'draft', 'out_for_sig', 'signed', 'exchanged', 'cancelled'].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === s ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`doc-filter-${s}`}
          >
            {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="docs-empty-state">
            <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            <div className="text-sm font-medium text-slate-700 mb-1">
              No paperwork here yet
            </div>
            <p className="text-xs text-slate-500 max-w-md mx-auto mb-4">
              F2F encounter forms, CMS 485 Plans of Care, NOAs and consents will show
              up as soon as a hospital places a referral. You can also start one
              manually against an existing placement.
            </p>
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-800"
              data-testid="docs-empty-create-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Start a paperwork item
            </button>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="doc-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">Document</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Signers</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const signedCount = (d.signers || []).filter((s) => s.signed_at).length;
                const totalSigners = (d.signers || []).length;
                return (
                  <tr key={d.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                      data-testid={`doc-row-${d.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{d.title}</div>
                      <div className="text-xs text-slate-400 font-mono">{d.id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{d.doc_type}</td>
                    <td className="px-4 py-3"><StatusPill status={d.status} /></td>
                    <td className="px-4 py-3 text-slate-700 text-xs">
                      {signedCount} / {totalSigners}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DocActions doc={d} onAction={callDocAction} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <CreateDocModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }}
          token={token}
        />
      )}
    </div>
  );
}

const DocActions = ({ doc, onAction }) => {
  if (doc.status === 'draft') {
    return (
      <div className="inline-flex gap-2 justify-end">
        <button
          onClick={() => onAction(doc.id, 'route-for-signature')}
          className="text-xs px-3 py-1 rounded-full bg-blue-600 text-white hover:bg-blue-700"
          data-testid={`route-btn-${doc.id}`}
        >
          <Send className="w-3 h-3 inline mr-1" /> Route
        </button>
        <button
          onClick={() => onAction(doc.id, 'cancel')}
          className="text-xs px-3 py-1 rounded-full bg-slate-200 text-slate-700 hover:bg-slate-300"
          data-testid={`cancel-btn-${doc.id}`}
        >
          <Ban className="w-3 h-3 inline mr-1" /> Cancel
        </button>
      </div>
    );
  }
  if (doc.status === 'out_for_sig') {
    const unsigned = (doc.signers || []).find((s) => !s.signed_at);
    return (
      <div className="inline-flex gap-2 justify-end">
        {unsigned && (
          <button
            onClick={() => onAction(doc.id, 'sign', { role: unsigned.role, method: 'esign' })}
            className="text-xs px-3 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid={`sign-btn-${doc.id}`}
          >
            <CheckCircle2 className="w-3 h-3 inline mr-1" /> Sign as {unsigned.role}
          </button>
        )}
      </div>
    );
  }
  if (doc.status === 'signed') {
    return (
      <button
        onClick={() => onAction(doc.id, 'exchange')}
        className="text-xs px-3 py-1 rounded-full bg-slate-900 text-white hover:bg-slate-800"
        data-testid={`exchange-btn-${doc.id}`}
      >
        <ArrowUpRightFromSquare className="w-3 h-3 inline mr-1" /> Exchange
      </button>
    );
  }
  return <span className="text-xs text-slate-400">—</span>;
};

const CreateDocModal = ({ onClose, onCreated, token }) => {
  const [placementId, setPlacementId] = useState('');
  const [docType, setDocType] = useState('F2F');
  const [title, setTitle] = useState('Face-to-Face Encounter');
  const [signers, setSigners] = useState([
    { role: 'physician', name: '' },
    { role: 'patient', name: '' },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!placementId.trim()) {
      toast.error('Placement ID required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/provider/documents`,
        { placement_id: placementId.trim(), doc_type: docType, title, signers,
          notes: null },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Document created');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="create-doc-modal">
        <DialogHeader>
          <DialogTitle>New paperwork item</DialogTitle>
          <DialogDescription>Create a draft document tied to a placement.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="placement-id">Placement ID</Label>
            <Input id="placement-id" value={placementId} onChange={(e) => setPlacementId(e.target.value)}
                    placeholder="uuid…" data-testid="create-doc-placement-id" />
          </div>
          <div>
            <Label>Document type</Label>
            <Select value={docType} onValueChange={(v) => {
              setDocType(v);
              const t = DOC_TYPES.find((x) => x.v === v);
              if (t) setTitle(t.l);
            }}>
              <SelectTrigger data-testid="create-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="doc-title">Title</Label>
            <Input id="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-full border border-slate-200 text-slate-700">
              Cancel
            </button>
            <button onClick={submit} disabled={submitting}
              className="px-4 py-2 text-sm rounded-full bg-slate-900 text-white disabled:opacity-50"
              data-testid="create-doc-submit">
              {submitting ? 'Creating…' : 'Create draft'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
